import React, { useEffect, useState, useRef } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Paper, Stack } from '@mui/material'
import { Button, ButtonGroup, Container, Col, Row } from 'react-bootstrap'
import moment from 'moment'

import useFetchData from 'Shared/Hooks/useFetchData'
import DataTable from 'Shared/Components/DataTable'
import { apiUrl } from 'Shared/utils'
import BackLinkButton from 'Shared/Components/BackLinkButton'
import { FloatingInputDisabled, FloatingDateRangePicker } from 'Shared/Components/FloatingLabel/FloatingLabel'

const SmartlockHistory = () => {
  const { t } = useTranslation()
  const { send } = useFetchData()
  const { setPageLoading } = useOutletContext()
  const { device_id } = useParams()

  const [deviceName, setDeviceName] = useState()  

  const [statusHistory, setStatusHistory] = useState({})
  const statusTableRef = useRef()

  const [eventHistory, setEventHistory] = useState({})
  const eventTableRef = useRef()

  const [commandHistory, setCommandHistory] = useState({})
  const commandTableRef = useRef()

  const [date, setDate] = useState()
  
  const loadDeivceName = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smart_lock/device`,
      params: {
        "device_id": device_id
      },
      returnType: "json"
    })

    body && setDeviceName(body["device_name"])
  }

  const getStatusHistory = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smart_lock/status_history`,
      params: {
        "device_id": device_id,
        "start_time": date ? moment(date[0]).unix() : "",
        "end_time": date ? moment(date[1]).unix() : "",
        "offset": (statusTableRef.current.getState()["page"]-1)*statusTableRef.current.getState()["itemsPerPage"],
        "limit": statusTableRef.current.getState()["itemsPerPage"]
      },
      returnType: "json"
    })

    body && setStatusHistory({
      "data": body["data"].map((i) => ({
        "datetime": i["datetime"],
        "status": i["status"] ? t("smartlock_locked") : t("smartlock_opened"),
      })),
      "count": Math.ceil(body["count"]/statusTableRef.current.getState()["itemsPerPage"])
    })
  }

  const getEventHistory = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smart_lock/event_history`,
      params: {
        "device_id": device_id,
        "start_time": date ? moment(date[0]).unix() : "",
        "end_time": date ? moment(date[1]).unix() : "",
        "offset": (eventTableRef.current.getState()["page"]-1)*eventTableRef.current.getState()["itemsPerPage"],
        "limit": eventTableRef.current.getState()["itemsPerPage"]
      },
      returnType: "json"
    })

    body && setEventHistory({
      "data": body["data"].map((i) => ({
        "datetime": i["datetime"],
        "event": i["event"],
        "authorized": i["authorized"] ? t("general_yes") : t("general_no"),
        "value": i["value"]
      })),
      "count": Math.ceil(body["count"]/eventTableRef.current.getState()["itemsPerPage"])
    })
  }

  const getCommandHistory = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smart_lock/command_history`,
      params: {
        "device_id": device_id,
        "start_time": date ? moment(date[0]).unix() : "",
        "end_time": date ? moment(date[1]).unix() : "",
        "offset": (commandTableRef.current.getState()["page"]-1)*commandTableRef.current.getState()["itemsPerPage"],
        "limit": commandTableRef.current.getState()["itemsPerPage"]
      },
      returnType: "json"
    })

    body && setCommandHistory({
      "data": body["data"],
      "count": Math.ceil(body["count"]/commandTableRef.current.getState()["itemsPerPage"])
    })
  }

  const loadStatusData = async () => {
    statusTableRef.current.setTableLoading(true)

    await getStatusHistory(false)

    statusTableRef.current.setTableLoading(false)
  }

  const loadEventData = async () => {
    eventTableRef.current.setTableLoading(true)

    await getEventHistory(false)

    eventTableRef.current.setTableLoading(false)
  }

  const loadCommandData = async () => {
    commandTableRef.current.setTableLoading(true)

    await getCommandHistory(false)

    commandTableRef.current.setTableLoading(false)
  }

  const update = async () => {
    setPageLoading(true)

    await Promise.all([
      getStatusHistory(),
      getEventHistory(),
      getCommandHistory()
    ])

    setPageLoading(false)
  }

  useEffect(() => {
    const init = async () => {
      setPageLoading(true)

      await Promise.all([
        loadDeivceName(),
        getStatusHistory(),
        getEventHistory(),
        getCommandHistory()
      ])

      setPageLoading(false)
    }
    
    init()
  }, [])

  return (<>
    <div className="mainContainer"  style={{ borderRadius: "15px", backgroundColor: "white", padding: "20px" }}>
      <div style={{ width: "100%", height: "100%", overflow: "auto" }}>
        <BackLinkButton />

        <Container fluid style={{ padding: "0px 15px 0px 15px" }}>
          <Row className="mt-1 mb-1">
            <Col xs={12} lg={4}>
              <FloatingInputDisabled label="Device Name" name="device" value={deviceName} />
            </Col>

            <Col xs={12} lg={4}>
              <FloatingDateRangePicker label="Date Range" name="date" value={date} onChange={(d) => setDate([new Date(d[0]), new Date(d[1])]) }/>
            </Col>

            <Col xs={12} className="mt-2">
              <ButtonGroup style={{ width: "100%" }}>
                <Button variant="success" onClick={() => update()}>{t("general_search")}</Button>
                {/* <Button variant="info" onClick={() => report()}>{t("general_export")}</Button> */}
              </ButtonGroup>
            </Col>
          </Row>

          <Row style={{ marginTop: "20px" }}>
            <Col xs={12} style={{ height: "100%", padding: "10px" }}>
              <Stack spacing={3}>
                <div style={{ height: "400px", border: "1px solid black", borderRadius: "8px", display: "flex", flexDirection: "column", padding: "10px" }}>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    <b>{t("smartlock_cmdHistory")}</b>
                  </div>

                  <Paper elevation={0} sx={{ flex: 1, display: "flex", flexFlow: "column", overflow: "auto" }}>
                    <DataTable
                      data={commandHistory["data"]} count={commandHistory["count"]} ref={commandTableRef}
                      loadData={loadCommandData}
                      columns={{
                        "datetime": { "label": t("general_datetime"), "sorting": false, "nowrap": true },
                        "username": { "label": t("users_username"), "sorting": false, "nowrap": true },
                      }}
                    />
                  </Paper>
                </div>

                <div style={{ height: "400px", border: "1px solid black", borderRadius: "8px", display: "flex", flexDirection: "column", padding: "10px" }}>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    <b>{t("smartlock_eventHistory")}</b>
                  </div>

                  <Paper elevation={0} sx={{ flex: 1, display: "flex", flexFlow: "column", overflow: "auto" }}>
                    <DataTable
                      data={eventHistory["data"]} count={eventHistory["count"]} ref={eventTableRef}
                      loadData={loadEventData}
                      columns={{
                        "datetime": { "label": t("general_datetime"), "sorting": false, "nowrap": true },
                        "event": { "label": t("smartlock_eventName"), "sorting": false, "nowrap": true },
                        "value": { "label": t("smartlock_value"), "sorting": false, "nowrap": true },
                        "authorized": { "label": t("smartlock_eventAuthorized"), "sorting": false, "nowrap": true },
                      }}
                    />
                  </Paper>
                </div>

                <div style={{ height: "400px", border: "1px solid black", borderRadius: "8px", display: "flex", flexDirection: "column", padding: "10px" }}>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    <b>{t("smartlock_statusHistory")}</b>
                  </div>

                  <Paper elevation={0} sx={{ flex: 1, display: "flex", flexFlow: "column", overflow: "auto" }}>
                    <DataTable
                      data={statusHistory["data"]} count={statusHistory["count"]} ref={statusTableRef}
                      loadData={loadStatusData}
                      columns={{
                        "datetime": { "label": t("general_datetime"), "sorting": false, "nowrap": true },
                        "status": { "label": t("smartlock_status"), "sorting": false, "nowrap": true },
                      }}
                    />
                  </Paper>
                </div>
              </Stack>
            </Col>
          </Row>
        </Container> 
      </div>
    </div>
  </>)
}

export default SmartlockHistory